/* eslint-disable @typescript-eslint/unbound-method */
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { extend, isDefined, uid } from "@dhx/ts-common/core";
import { awaitRedraw, create, el, sv } from "@dhx/ts-common/dom";
import { eventHandler, locate, locateNodeByClassName } from "@dhx/ts-common/html";
import { View } from "@dhx/ts-common/view";
import { ContextMenu } from "@dhx/ts-menu";
import { Exporter } from "./helpers/Export";
import { nearestLinkPath } from "./helpers/linkPaths";
import { Selection } from "./helpers/Selection";
import { shapesFactory } from "./components/shapeFactory";
import { flowShapes } from "./components/Shapes/FlowShape";
import { ShapesCollection } from "./ShapesCollection";
import { Toolbar } from "./Toolbar";
import {
	DataEvents,
	DiagramEvents,
	ICoords,
	IDiagram,
	IDiagramConfig,
	ILineConfig,
	IBaseShape,
	SelectionEvents,
	IContentSize,
	IDiagramEventHandlersMap,
	ICustomShapeParam,
	IAutoPlacement,
	TreeDirection,
	IBaseCoords,
	ItemConfig,
	ShapeConfig,
} from "./types";
import Graph from "./helpers/autoplacement/Graph";
import { Direction } from "./helpers/autoplacement/types";
import Hola from "./helpers/autoplacement/layouts/Hola";
import { compose } from "./helpers/autoplacement/tasks/compose";
import { decompose } from "./helpers/autoplacement/tasks/decompose";
import { placeMindmap } from "./helpers/placement/mindmap";
import { placeOrgonogram } from "./helpers/placement/orgonogram";
import { anyFunction, Id, ISelectionEventsHandlersMap } from "@dhx/ts-common/types";
import { Swimlane } from "./components/Swimlane/Swimlane";
import { Group } from "./components/Groups/Group";
import { Editor, EditorEvents } from "./helpers/Editor";
import { GroupSwimlane } from "./components/Groups/GroupSwimlane";
import { CellManager, CellManagerEvents, ICellManagerHandlersMap } from "./helpers/CellManager";
import { IDataItem } from "@dhx/ts-data";
import { LineText } from "./components/Lines/LineText";
import { Line } from "./components/Lines/Line";
import {
	getDistanceEblowLine,
	getDistancePercentage,
	getLinePercentage,
	getNearPointStraightLine,
} from "./helpers/lineHelpers";

// taken from package.json
declare let VERSION: string;

export class Diagram extends View implements IDiagram {
	public version: string;
	public config: IDiagramConfig;
	public events: IEventSystem<
		DataEvents | SelectionEvents | DiagramEvents | EditorEvents | CellManagerEvents,
		IDiagramEventHandlersMap | ICellManagerHandlersMap | ISelectionEventsHandlersMap
	>;
	public data: ShapesCollection;
	public selection: Selection;
	public export: Exporter;
	public toolbar: Toolbar;
	public flowShapes;
	public editor: Editor;
	public cellManager: CellManager;
	public _lastItemClickCoords: IBaseCoords = null;

	private _htmlevents: any;
	private _diagramSize: any;
	private _active: boolean;
	private _orgTypes: string[] = ["org", "mindmap"];
	private _menu: ContextMenu;

	constructor(container: HTMLElement | any, config?: IDiagramConfig) {
		super(container, config);
		this.version = VERSION;

		this._set_defaults();
		this._init_struct();
		this._initEvents();

		if (this.config.toolbar) {
			this.toolbar = new Toolbar(this.events, this.config.toolbar);
		}
		const view = create({ render: vm => this._render(vm) }, this);
		this.mount(container, view);
	}
	public addShape(type: string, parameters: ICustomShapeParam): void {
		const allTypes = ["line", "dash", "card", "img-card", "text", ...Object.keys(this.flowShapes)];
		if (allTypes.includes(type)) {
			throw new Error("Cannot reassign existing type");
		}
		if (typeof parameters.template !== "function") {
			throw new Error("The template property must be set as a function");
		}
		if (parameters.defaults) {
			this.config.defaults[type] = { ...parameters.defaults };
		}
		if (parameters.properties) {
			this.config.properties[type] = [...parameters.properties];
		}
		if (parameters.eventHandlers) {
			for (const [event_name, events] of Object.entries(parameters.eventHandlers)) {
				this._htmlevents[event_name] = eventHandler(
					event => {
						const item = { ...this.data.getItem(locate(event)) };
						Object.keys(item).forEach(key => {
							if (key.startsWith("$") || key === "preview") {
								delete item[key];
							}
						});
						return item;
					},
					events,
					this._htmlevents[event_name] as anyFunction
				);
			}
		}
		this.flowShapes[type] = parameters.template;
	}
	public locate(event: Event): IBaseShape {
		const id = locate(event, "dhx_id");
		const item = this.data.getItem(id);
		return item ? item.$item : null;
	}
	public collapseItem(id: Id, dir?: TreeDirection): void {
		if (this.events.fire(DiagramEvents.beforeCollapse, [id, dir])) {
			const shape = this.data.getItem(id);
			if (
				this.config.type === "mindmap" &&
				shape.$level === 0 &&
				shape.openDir &&
				(dir === "left" || dir === "right")
			) {
				this.data.update(id, {
					openDir: {
						right: dir === "right" ? false : !!shape.openDir.right,
						left: dir === "left" ? false : !!shape.openDir.left,
					},
				});
				if (shape.$connection.length) {
					shape.$connection.forEach(item => {
						const child = this.data.getItem(item[1]);
						if (child.dir && child.dir.toLowerCase().includes(dir)) {
							this.data.update(
								child.id,
								{
									hidden: true,
								},
								true
							);
						}
					});
				}
			} else {
				this.data.update(id, { open: false });
			}
			this.events.fire(DiagramEvents.afterCollapse, [id, dir]);
		}
		this.paint();
	}
	public expandItem(id: Id, dir?: TreeDirection): void {
		const shape = this.data.getItem(id);
		if (shape?.open && !shape.openDir) return;

		if (this.events.fire(DiagramEvents.beforeExpand, [id, dir])) {
			if (
				this.config.type === "mindmap" &&
				shape.$level === 0 &&
				shape.openDir &&
				(dir === "left" || dir === "right")
			) {
				this.data.update(id, {
					open: true,
					openDir: {
						right: dir === "right" ? true : !!shape.openDir.right,
						left: dir === "left" ? true : !!shape.openDir.left,
					},
				});
				if (shape.$connection.length) {
					shape.$connection.forEach(item => {
						const child = this.data.getItem(item[1]);
						if (child.dir && child.dir.toLowerCase().includes(dir)) {
							this.data.update(
								child.id,
								{
									hidden: false,
								},
								true
							);
						}
					});
				}
			} else {
				this.data.update(id, { open: true });
			}
			this.events.fire(DiagramEvents.afterExpand, [id, dir]);

			const item = this.data.getItem(id);
			if (item && item.parent) {
				this.expandItem(item.parent);
			}
		}
		this.paint();
	}
	public getScrollState(): ICoords {
		const wrapper = this.getRootView().node.el;
		return {
			x: Math.round(wrapper.scrollLeft * this.config.scale),
			y: Math.round(wrapper.scrollTop * this.config.scale),
		};
	}
	public scrollTo(x: number, y: number): void {
		const wrapper = this.getRootView().node.el;
		wrapper.scrollLeft = Math.round(x * this.config.scale);
		wrapper.scrollTop = Math.round(y * this.config.scale);
	}
	public showItem(id: Id, dir?: TreeDirection): void {
		const item: ShapeConfig = this.data.getItem(id);
		if (!item) {
			return;
		}
		if (item.parent) {
			this.expandItem(item.parent, dir);
		}
		awaitRedraw().then(() => {
			const wrapper = this.getRootView().node.el;
			const box = item.$item.getBox();
			const width = wrapper.offsetWidth / 2;
			const height = wrapper.offsetHeight / 2;
			this.scrollTo(box.right + 10 - width, box.bottom + 10 - height);
		});
	}
	public autoPlace(config?: IAutoPlacement) {
		const { autoplacement, type } = this.config;
		const mode = config?.mode || autoplacement?.mode || "direct";
		const root = config?.root || this.selection.getId();

		if (this._orgTypes.includes(type)) {
			throw new Error("This method does not work with this type of diagram");
		}

		const graph = new Graph();
		this.data.forEach((item: ItemConfig) => {
			const itemType = item.$item.getBaseType();
			if (itemType === "shape") {
				graph.addNode({
					id: item.id.toString(),
					w: item.width,
					h: item.height,
					x: item.x,
					y: item.y,
				});
			}
		});

		this.data.forEach((item: ItemConfig) => {
			const itemType = item.$item.getBaseType();
			if (itemType === "line" && item.from && item.to) {
				this.data.update(item.id, {
					fromSide: mode === "direct" ? "center" : undefined,
					toSide: mode === "direct" ? "center" : undefined,
				});
				graph.addEdge(item.from.toString(), item.to.toString());
			} else if (itemType === "line") {
				this.data.remove(item.id);
			}
		});

		graph.clean();

		const layoutTree = new Hola();
		const composeGraph = compose(
			decompose(graph).map(graphItem => {
				graphItem = layoutTree.layout(graphItem, {
					mode,
					root,
					dir: autoplacement?.direction || Direction.Bottom,
					wide: autoplacement?.wide,
					itemPadding: autoplacement?.itemPadding || 0,
					levelPadding: autoplacement?.levelPadding || 0,
					full: true,
					preserveLocation: false,
				});

				graphItem.setGlobalBox();
				return graphItem;
			}),
			{
				padding:
					typeof autoplacement?.graphPadding === "number"
						? config?.graphPadding || autoplacement?.graphPadding
						: 200,
			}
		);

		const bounds = composeGraph.getBox();
		composeGraph.translate({ x: -bounds[0][0], y: -bounds[1][0] });

		composeGraph.getNodes().forEach(node => {
			const x = Math.floor(node.x - node.w / 2);
			const y = Math.floor(node.y - node.h / 2);
			this.data.update(node.id, { x, y });
		});
	}
	public getCurrentCoords({ clientX, clientY }: MouseEvent): IBaseCoords {
		const { scale, margin } = this.config;
		const container = this.getRootView().node.el;
		const diagramRect = container.getBoundingClientRect();
		if (!container || !diagramRect) {
			return;
		}
		const size = this._diagramSize || { left: -margin.x, top: -margin.y };
		const scrolls = {
			x: container.scrollLeft,
			y: container.scrollTop,
		};
		return {
			x: Math.round(
				(clientX - diagramRect.left + scrolls.x) / scale - margin.x + (size.left + margin.x)
			),
			y: Math.round((clientY - diagramRect.top + scrolls.y) / scale - margin.y + (size.top + margin.y)),
		};
	}
	public findNearestConnector(event: MouseEvent): IDataItem {
		const coords = this.getCurrentCoords(event);
		const fitLines = [];

		this.data.map(item => {
			if (item.$item.getBaseType() === "line" && item?.points?.length) {
				for (let i = 0; i < item.points.length; i++) {
					const point = item.points[i];
					if (item.points[i + 1]) {
						const a = coords;
						const b = point;
						const c = item.points[i + 1];

						const gap = 8;

						const byY = (b.y < a.y && c.y > a.y) || (c.y < a.y && b.y > a.y);
						const byX = (b.x < a.x && c.x > a.x) || (c.x < a.x && b.x > a.x);

						if (byY || byX) {
							const ab = Math.sqrt(Math.pow(b.x - a.x, 2) + Math.pow(b.y - a.y, 2));
							const ac = Math.sqrt(Math.pow(c.x - a.x, 2) + Math.pow(c.y - a.y, 2));
							const cb = Math.sqrt(Math.pow(c.x - b.x, 2) + Math.pow(c.y - b.y, 2));

							const p = (ab + ac + cb) / 2;
							const S = Math.sqrt(p * (p - ab) * (p - ac) * (p - cb));

							const h = (2 * S) / cb;
							if (h <= gap) {
								fitLines.push(item);
								break;
							}
						}
					}
				}
			}
		});

		if (fitLines.length) {
			return fitLines[fitLines.length - 1];
		}
	}
	public destructor(): void {
		this.editor && this.editor.destructor();
		this.events.clear();
		this.unmount();
	}
	protected _render(vm) {
		if (this._doNotRepaint && vm.node) {
			return vm.node;
		}

		const { size, svgContent, htmlContent } = this._getContent();

		this.events.fire(DiagramEvents.beforeRender, [size]);
		this._diagramSize = size;

		const { margin, type, scale } = this.config;

		const width = size.x - size.left + margin.x * 2;
		const height = size.y - size.top + margin.y * 2;
		const css = this._orgTypes.includes(type) ? "dhx_org_chart" : "dhx_free_diagram";

		size.left -= margin.x;
		size.top -= margin.y;

		const topHtmlPosition = size.top + margin.y < 0 ? Math.abs(size.top) : margin.y;
		const leftHtmlPosition = size.left + margin.x < 0 ? Math.abs(size.left) : margin.x;

		let toolbar = null;
		if (this.toolbar && this._lastItemClickCoords?.x && this._lastItemClickCoords?.y) {
			const id = this.selection.getId();
			if (id) {
				const item = this.data.getItem(id);
				toolbar = this.toolbar.render(item, { ...size, scale }, this._lastItemClickCoords);
			}
		}
		let extra = [];
		if (this.config.$svg) {
			extra = this.config.$svg(size);
		}
		return el(
			".dhx_diagram.dhx_widget",
			{
				...this._htmlevents,
				dhx_widget_id: this._uid,
			},
			[
				el(
					"div.dhx_diagram__container",
					{
						class: css,
						style: {
							height: height * scale,
							width: width * scale,
						},
					},
					[
						el(
							".dhx_diagram__wrapper.dhx_wrapper",
							{
								style: {
									transform: `scale(${scale})`,
									position: "absolute",
									top: 0,
									left: 0,
									transformOrigin: "top",
									zIndex: this._orgTypes.includes(type) ? (this._active ? 1 : 0) : 0,
								},
							},
							[
								el("div.dhx_diagram__scale-container", [
									sv(
										"svg",
										{
											xmlns: "http://www.w3.org/2000/svg",
											width,
											height,
											viewBox: `${size.left} ${size.top} ${width} ${height}`,
										},
										[
											sv("defs", [
												sv(
													"filter",
													{
														x: 0,
														y: 0,
														width: 1,
														height: 1,
														id: "dhx_text_background",
													},
													[
														sv("feFlood", { "flood-color": "white" }),
														sv("feComposite", { in: "SourceGraphic" }),
													]
												),
											]),
											sv("g", svgContent),
										]
									),
									el(
										".dhx_shape-container",
										{
											style: {
												position: "absolute",
												top: topHtmlPosition,
												left: leftHtmlPosition,
											},
										},
										htmlContent
									),
								]),
							]
						),
						extra,
						toolbar,
					]
				),
			]
		);
	}
	protected _set_defaults() {
		let defaultShapeType;
		switch (this.config.type) {
			case "mindmap":
				defaultShapeType = "topic";
				break;
			case "org":
				defaultShapeType = "card";
				break;
			default:
				defaultShapeType = "rectangle";
				break;
		}

		this.config = extend(
			{
				type: "default",
				defaultShapeType: this.config?.defaultShapeType || defaultShapeType,
				defaultLinkType: "line",
				lineGap: 10,
				scale: 1,
				margin: {
					x: 40,
					y: 40,
					itemX: 40,
					itemY: this.config?.type === "mindmap" ? 20 : 40,
				},
				gridStep: 10,
				defaults: {},
				properties: {},
				autoplacement: {
					mode: "direct",
					graphPadding: 200,
				},
				exportStyles: true,
			},
			this.config
		);
	}
	protected _initEventsHandlers() {
		this._htmlevents = {
			onscroll: () => {
				this.events.fire(DiagramEvents.scroll, [this.getScrollState()]);
			},
			onmousedown: eventHandler(event => this.locate(event), {
				dhx_diagram: event => {
					const target = event.target;
					const isContainer =
						target.getAttribute && (target.getAttribute("class") || "").match(/dhx_diagram\b/);
					const isSvg = target.tagName === "svg";
					if (!locateNodeByClassName(target, "dhx_popup_toolbar") && (isContainer || isSvg)) {
						const item = this.findNearestConnector(event);
						if (item) {
							this.events.fire(DiagramEvents.lineMouseDown, [item.id, event]);
							this.events.fire(DiagramEvents.itemMouseDown, [item.id, event]);

							if (this.config.select && !event.shiftKey && !event.altKey) {
								this.selection.add(item.id);
							}
						} else {
							this.events.fire(DiagramEvents.emptyAreaMouseDown, [event]);
						}
					}
				},
				dhx_diagram_line_title: (event: MouseEvent) => {
					const lineId: Id = locate(event, "parent_id");
					const titleId: Id = locate(event, "dhx_id");

					this.selection.add(lineId, titleId);
					this.events.fire(DiagramEvents.lineTitleMouseDown, [lineId, titleId, event]);
				},
				dhx_diagram_shape: (event, item) => {
					this.events.fire(DiagramEvents.shapeMouseDown, [
						item.id,
						event,
						this._getPoint(event.clientX, event.clientY),
					]);
					this.events.fire(DiagramEvents.itemMouseDown, [
						item.id,
						event,
						this._getPoint(event.clientX, event.clientY),
					]);
				},
				dhx_diagram_line: (event, item) => {
					this.events.fire(DiagramEvents.lineMouseDown, [
						item.id,
						event,
						this._getPoint(event.clientX, event.clientY),
					]);
					this.events.fire(DiagramEvents.itemMouseDown, [
						item.id,
						event,
						this._getPoint(event.clientX, event.clientY),
					]);
				},
				dhx_diagram_group: (event, item) => {
					this.events.fire(DiagramEvents.groupMouseDown, [
						item.id,
						event,
						this._getPoint(event.clientX, event.clientY),
					]);
					this.events.fire(DiagramEvents.itemMouseDown, [
						item.id,
						event,
						this._getPoint(event.clientX, event.clientY),
					]);
				},
			}),
			onmouseout: eventHandler(event => this.locate(event), {
				dhx_selected: () => {
					this._active = false;
					this.paint();
				},
				dhx_diagram_item: (event, item) => {
					if (!item) return;
					this.events.fire(DiagramEvents.itemMouseOut, [item.id, event]);
				},
			}),
			onmouseover: eventHandler(event => this.locate(event), {
				dhx_selected: () => {
					this._active = true;
					this.paint();
				},
				dhx_diagram_item: (event, item) => {
					if (!item) return;
					this.events.fire(DiagramEvents.itemMouseOver, [item.id, event]);

					/** @deprecated See a documentation: https://docs.dhtmlx.com/ */
					this.events.fire(DiagramEvents.shapeHover, [item.id, event]);
				},
			}),
			onclick: eventHandler(event => this.locate(event), {
				dhx_diagram_menu_icon: (event, item) => {
					const subHeaderId = locate(event, "subheader_id");
					if (!this.events.fire(DiagramEvents.beforeSubmenuOpen, [item.id, event, subHeaderId])) {
						return;
					}

					let subHeaderRows = [];
					if (item.config?.subHeaderRows?.enable && item.config?.subHeaderRows?.headers?.length) {
						subHeaderRows = item.config.subHeaderRows.headers;
					}
					let subHeaderCols = [];
					if (item.config?.subHeaderCols?.enable && item.config?.subHeaderCols?.headers?.length) {
						subHeaderCols = item.config.subHeaderCols.headers;
					}
					const subHeaders = [...subHeaderRows, ...subHeaderCols];
					const { $subMenu, id } = subHeaders.find(header => header.id === subHeaderId);

					this.cellManager.setSwimlane(item.id);
					const cellIndex = this.cellManager.getSubHeaderCellIndex(id);

					$subMenu.data.forEach(data => {
						if (
							data["remove"] &&
							!this.cellManager.validation(cellIndex, data.remove, "remove")
						) {
							data.disabled = true;
						}
						if (data["add"] && !this.cellManager.validation(cellIndex, data.add, "add")) {
							data.disabled = true;
						}
						if (data["move"] && !this.cellManager.validation(cellIndex, data.move, "move")) {
							data.disabled = true;
						}
					});

					this._menu.data.parse($subMenu.data);
					this._menu.showAt(event);

					this.events.fire(DiagramEvents.afterSubmenuOpen, [item.id, event, subHeaderId]);
				},
				dhx_expand_icon: (event, item) => {
					if (event.target.classList.contains("icon-left")) {
						this.expandItem(item.id, "left");
					} else if (event.target.classList.contains("icon-right")) {
						this.expandItem(item.id, "right");
					} else {
						this.expandItem(item.id);
					}

					if (this.config.select && !event.shiftKey && !event.altKey) {
						this.selection.add(item.id);
					}
				},
				dhx_hide_icon: (event, item) => {
					if (event.target.classList.contains("icon-left")) {
						this.collapseItem(item.id, "left");
					} else if (event.target.classList.contains("icon-right")) {
						this.collapseItem(item.id, "right");
					} else {
						this.collapseItem(item.id);
					}

					if (this.config.select && !event.shiftKey && !event.altKey) {
						this.selection.add(item.id);
					}
				},
				dhx_diagram: event => {
					const target = event.target;
					const isContainer =
						target.getAttribute && (target.getAttribute("class") || "").match(/dhx_diagram\b/);
					const isSvg = target.tagName === "svg";
					if (isContainer || isSvg) {
						const item = this.findNearestConnector(event);
						if (item) {
							this.events.fire(DiagramEvents.lineClick, [item.id, event]);
							this.events.fire(DiagramEvents.itemClick, [item.id, event]);

							if (
								this.config.select &&
								!event.shiftKey &&
								!event.altKey &&
								item.id !== this.selection.getId()
							) {
								this.selection.add(item.id);
							}
						} else {
							this.events.fire(DiagramEvents.emptyAreaClick, [event]);
						}
					}
				},
				dhx_diagram_line_title: (event: MouseEvent) => {
					const lineId: Id = locate(event, "parent_id");
					const titleId: Id = locate(event, "dhx_id");

					this.events.fire(DiagramEvents.lineTitleClick, [lineId, titleId, event]);
				},
				dhx_diagram_shape: (event, item) => {
					this.events.fire(DiagramEvents.shapeClick, [item.id, event]);
					this.events.fire(DiagramEvents.itemClick, [item.id, event]);

					if (this.config.select && !event.shiftKey && !event.altKey) {
						this.selection.add(item.id);
					}
				},
				dhx_diagram_line: (event, item) => {
					this.events.fire(DiagramEvents.lineClick, [item.id, event]);
					this.events.fire(DiagramEvents.itemClick, [item.id, event]);

					if (this.config.select && !event.shiftKey && !event.altKey) {
						this.selection.add(item.id);
					}
				},
				dhx_diagram_group: (event, item) => {
					this.events.fire(DiagramEvents.groupClick, [item.id, event]);
					this.events.fire(DiagramEvents.itemClick, [item.id, event]);

					if (this.config.select && !event.shiftKey && !event.altKey) {
						this.selection.add(item.id);
					}
				},
				dhx_diagram_group__header: (event, item) => {
					this.events.fire(DiagramEvents.groupHeaderClick, [item.id, event]);
					this.events.fire(DiagramEvents.groupClick, [item.id, event]);
					this.events.fire(DiagramEvents.itemClick, [item.id, event]);

					if (this.config.select && !event.shiftKey && !event.altKey) {
						this.selection.add(item.id);
					}
				},
				dhx_diagram_swimlane__subheader: (event, item) => {
					const subHeaderId = locate(event, "subheader_id");

					this.events.fire(DiagramEvents.groupHeaderClick, [item.id, event, subHeaderId]);
					this.events.fire(DiagramEvents.groupClick, [item.id, event]);
					this.events.fire(DiagramEvents.itemClick, [item.id, event]);

					if (this.config.select && !event.shiftKey && !event.altKey) {
						this.selection.add(item.id);
					}
				},
			}),
			ondblclick: eventHandler(event => this.locate(event), {
				dhx_diagram: event => {
					const target = event.target;
					const isContainer =
						target.getAttribute && (target.getAttribute("class") || "").match(/dhx_diagram\b/);
					const isSvg = target.tagName === "svg";
					if (isContainer || isSvg) {
						const item = this.findNearestConnector(event);
						if (item) {
							this.events.fire(DiagramEvents.lineDblClick, [item.id, event]);
							this.events.fire(DiagramEvents.itemDblClick, [item.id, event]);

							if (
								this.config.select &&
								!event.shiftKey &&
								!event.altKey &&
								item.id !== this.selection.getId()
							) {
								this.selection.add(item.id);
							}
						} else {
							this.events.fire(DiagramEvents.emptyAreaDblClick, [event]);
						}
					}
				},
				dhx_diagram_line_title: (event: MouseEvent) => {
					const lineId: Id = locate(event, "parent_id");
					const titleId: Id = locate(event, "dhx_id");

					const item = this.data.getItem(lineId).title.text.find(({ id }) => id === titleId).$item;
					if (item.isEditable() && this.editor.openEditor(lineId, "text", titleId)) {
						item.setEditorNode(this.editor.render(), "text");
					}
					this.events.fire(DiagramEvents.lineTitleDblClick, [lineId, titleId, event]);
				},
				dhx_diagram_shape: (event, item) => {
					this.events.fire(DiagramEvents.shapeDblClick, [item.id, event]);
					this.events.fire(DiagramEvents.itemDblClick, [item.id, event]);
				},
				dhx_diagram_line: (event, item) => {
					this.events.fire(DiagramEvents.lineDblClick, [item.id, event]);
					this.events.fire(DiagramEvents.itemDblClick, [item.id, event]);
				},
				dhx_diagram_group: (event, item) => {
					this.events.fire(DiagramEvents.groupDblClick, [item.id, event]);
					this.events.fire(DiagramEvents.itemDblClick, [item.id, event]);
				},
				dhx_diagram_group__header: (event, item) => {
					this.events.fire(DiagramEvents.groupDblClick, [item.id, event]);
					this.events.fire(DiagramEvents.itemDblClick, [item.id, event]);
					this.events.fire(DiagramEvents.groupHeaderDblClick, [item.id, event]);

					if (
						locateNodeByClassName(event, "dhx_diagram_group__header_text") &&
						item.isEditable() &&
						this.editor.openEditor(item.id)
					) {
						item.setEditorNode(this.editor.render(), "text", item.id);
					}
				},
				dhx_diagram_swimlane__subheader: (event, item) => {
					const subHeaderId = locate(event, "subheader_id");

					this.events.fire(DiagramEvents.groupDblClick, [item.id, event]);
					this.events.fire(DiagramEvents.itemDblClick, [item.id, event]);
					this.events.fire(DiagramEvents.groupHeaderDblClick, [item.id, event, subHeaderId]);

					if (
						locateNodeByClassName(event, "dhx_diagram_swimlane__subheader_text") &&
						item.isEditable(subHeaderId) &&
						this.editor.openEditor(item.id, "text", subHeaderId)
					) {
						item.setEditorNode(this.editor.render(), "text", subHeaderId);
					}
				},
				dhx_diagram_item__text: (event, item) => {
					this.events.fire(DiagramEvents.shapeDblClick, [item.id, event]);
					this.events.fire(DiagramEvents.itemDblClick, [item.id, event]);

					if (item.isEditable() && this.editor.openEditor(item.id)) {
						item.setEditorNode(this.editor.render(), "text");
					}
				},
				dhx_diagram_item__title: (event, item) => {
					this.events.fire(DiagramEvents.shapeDblClick, [item.id, event]);
					this.events.fire(DiagramEvents.itemDblClick, [item.id, event]);

					if (item.isEditable() && this.editor.openEditor(item.id, "title")) {
						item.setEditorNode(this.editor.render(), "title");
					}
				},
			}),
		};
	}
	protected _init_struct() {
		this.events = new EventSystem<DataEvents | SelectionEvents | DiagramEvents, IDiagramEventHandlersMap>(
			this
		);
		this.flowShapes = { ...flowShapes };
		this.data = new ShapesCollection(
			{
				init: item => {
					if (!item.id) item.id = uid();

					const defType =
						"from" in item ? this.config.defaultLinkType : this.config.defaultShapeType;
					item.type = item.type || defType;

					switch (item.type) {
						case "$group":
							item.$item = new Group(item, {
								data: this.data,
							});
							break;
						case "$swimlane":
							item.$item = new Swimlane(item, {
								data: this.data,
							});
							break;
						case "$sgroup":
							item.$item = new GroupSwimlane(item, {
								data: this.data,
							});
							break;
						case "line":
						case "dash":
							if (!this._orgTypes.includes(this.config.type) && item.from) {
								item.stroke = item.stroke || "#2196F3";
							}
							item.$item = new Line(item, {});
							if (item?.title?.text) {
								item.title.text = item.title.text.map(titleConfig => {
									titleConfig = {
										type: "$linetext",
										id: uid(),
										...titleConfig,
									};
									titleConfig.$item = new LineText(titleConfig, {
										parent: item,
									});

									return {
										...titleConfig,
										...titleConfig?.$item.config,
									};
								});
							}
							break;
						default:
							item.$item = shapesFactory(item, {
								defaults: this.config.defaults[item.type] || {},
								properties: this.config.properties[item.type] || {},
								shapes: this.flowShapes,
								data: this.data,
							});
							break;
					}

					return item;
				},
				update(item: ItemConfig) {
					item.$item.config = item;

					if (item.$item.getBaseType() === "line" && item?.title?.text) {
						item.title.text.forEach(title => {
							title.$item.parent = item;
							title.$item.config = title;
						});
					}
				},
				diagramConfig: this.config,
			},
			this.events
		);

		this.selection = new Selection({}, this.data, this.events);
		this.cellManager = new CellManager(this.data, this.events);
		this.export = new Exporter("diagram", this.version, this);
		this.editor = new Editor({ data: this.data, events: this.events });
		this._menu = new ContextMenu(null);
	}
	protected _initEvents() {
		this._initEventsHandlers();
		this.events.on(DiagramEvents.lineDblClick, (id: Id) => {
			if (!isDefined(id) || this.config.type !== "default") return;

			const line = this.data.getItem(id);
			const points = line.points;
			const cp = this._lastItemClickCoords;

			let distance = 50;

			if (line.connectType === "straight" || points.length === 2) {
				const sp = points[0];
				const ep = points[points.length - 1];
				const lp = getNearPointStraightLine(sp, ep, cp);
				const factor = getLinePercentage(sp, ep, lp);

				distance = getDistancePercentage(factor);
			} else {
				distance = getDistanceEblowLine(line, cp);
			}

			if (!line.title) {
				line.title = {};
				line.$item.setDefaults(line);
			}

			const titleId = uid();
			const lineTextConfig: any = {
				type: "$linetext",
				id: titleId,
				text: "Text",
				distance,
			};

			line.title.text.push(lineTextConfig);

			lineTextConfig.$item = new LineText(lineTextConfig, {
				data: this.data,
				parent: line,
			});

			line.title.text = line.title.text.map(text => {
				if (text.id === titleId) {
					return {
						...text,
						...lineTextConfig.$item.config,
					};
				}
				return text;
			});

			this.data.update(id, line);
			this.selection.add(id, titleId);
		});
		this.events.on(SelectionEvents.afterSelect, id => {
			if (this.data.getItem(id).$item.getBaseType() === "swimlane") {
				this.cellManager.setSwimlane(id);
			}
			this.paint();
		});
		this.events.on(DiagramEvents.itemMouseDown, (_id: Id, event: MouseEvent) => {
			this._lastItemClickCoords = this.getCurrentCoords(event);
			this.paint();
		});
		this.events.on(DiagramEvents.lineTitleMouseDown, (_lineId: Id, _titleId: Id, event: MouseEvent) => {
			this._lastItemClickCoords = this.getCurrentCoords(event);
			this.paint();
		});
		this.events.on(EditorEvents.beforeEditorOpen, () => this.paint());
		this.events.on(EditorEvents.afterEditorClose, () => this.paint());
		this.events.on(DataEvents.change, () => this.paint());

		this._menu.events.on("click", id => {
			const [swimlaneId, subheaderId, action] = (id as string).split("/");
			this.cellManager.setSwimlane(swimlaneId);
			const type = this.cellManager.getSubHeaderType(subheaderId);
			const cellIndex = this.cellManager.getSubHeaderCellIndex(subheaderId);

			switch (action) {
				case "move_column_left":
					this.cellManager.move(cellIndex, "left", true);
					break;
				case "move_column_right":
					this.cellManager.move(cellIndex, "right", true);
					break;
				case "move_row_up":
					this.cellManager.move(cellIndex, "up", true);
					break;
				case "move_row_down":
					this.cellManager.move(cellIndex, "down", true);
					break;
				case "add_column_left":
					this.cellManager.add(cellIndex, "left", true);
					break;
				case "add_column_right":
					this.cellManager.add(cellIndex, "right", true);
					break;
				case "add_row_up":
					this.cellManager.add(cellIndex, "up", true);
					break;
				case "add_row_down":
					this.cellManager.add(cellIndex, "down", true);
					break;
				case "remove":
					this.cellManager.remove(cellIndex, type, true);
					break;
			}
			this.paint();
		});
	}
	private _getContent() {
		switch (this.config.type) {
			case "org":
				placeOrgonogram(this.data, this.config);
				break;
			case "mindmap":
				placeMindmap(this.data, this.config);
				break;
			default:
				break;
		}
		const size: IContentSize = { x: 0, y: 0, left: 0, top: 0, scale: this.config.scale };
		const lines = [];
		const htmlContent = [];

		this.data.mapVisible((item: ItemConfig) => {
			const type = item?.$item.getBaseType();

			if (!item || item.$group || item.type === "$sgroup") {
				return;
			}

			if (type === "line") {
				if (this.config.type === "default") {
					nearestLinkPath(
						item as ILineConfig,
						this.data.getItem(item.from),
						this.data.getItem(item.to),
						this.config
					);
					if (item?.title?.text.length && !item?.title?.hidden) {
						item.title.text.forEach(text => {
							if (text.$item && !text.hidden) {
								htmlContent.push(text.$item.render());
							}
						});
					}
				}
				lines.push(item.$item.render());
			}

			if (type === "shape" || type === "group" || type === "swimlane") {
				htmlContent.push(item.$item.render());
			}

			const box = item.$item.getBox();
			if (box.right > size.x) {
				size.x = box.right;
			}
			if (box.left < size.left) {
				size.left = box.left;
			}
			if (box.bottom > size.y) {
				size.y = box.bottom;
			}
			if (box.top < size.top) {
				size.top = box.top;
			}
		});
		return { size, svgContent: lines, htmlContent };
	}
	private _getPoint(x: number, y: number): ICoords {
		const diagramRect = this.getRootView().node.el.getBoundingClientRect();
		return {
			x: x - diagramRect.left - this.config.margin.x,
			y: y - diagramRect.top - this.config.margin.y,
		};
	}
}
